import { css } from '@emotion/react';
import { useEffect } from 'react';
import { EDITED_SINCE_PUBLISH_HIGHLIGHT } from '../../constants/highlight';

export const editorStyles = css`
  .duke-tiptap-editor.tiptap mark.duke-edited-since-publish {
    background-color: ${EDITED_SINCE_PUBLISH_HIGHLIGHT.bg};
    color: ${EDITED_SINCE_PUBLISH_HIGHLIGHT.fg};
    border-radius: 0.125rem;
    padding: 0 0.125rem;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }
  .duke-tiptap-editor.tiptap {
    line-height: 1.5;
    outline: none;
    padding: 1rem;
  }
  .duke-tiptap-editor.tiptap > * + * {
    margin-top: 0.75em;
  }
  .duke-tiptap-editor.tiptap h1,
  .duke-tiptap-editor.tiptap h2,
  .duke-tiptap-editor.tiptap h3,
  .duke-tiptap-editor.tiptap h4,
  .duke-tiptap-editor.tiptap h5,
  .duke-tiptap-editor.tiptap h6 {
    line-height: 1.8;
    font-weight: 500;
  }
  .duke-tiptap-editor.tiptap h1 {
    font-size: 2em;
  }
  .duke-tiptap-editor.tiptap h2 {
    font-size: 1.5em;
  }
  .duke-tiptap-editor.tiptap h3 {
    font-size: 1.17em;
  }
  .duke-tiptap-editor.tiptap h4 {
    font-size: 1em;
  }
  .duke-tiptap-editor.tiptap h5 {
    font-size: 0.83em;
  }
  .duke-tiptap-editor.tiptap h6 {
    font-size: 0.67em;
  }
  .duke-tiptap-editor.tiptap a {
    color: #805ad5;
    text-decoration: none;
    cursor: pointer;
  }
  .duke-tiptap-editor.tiptap a:hover {
    text-decoration: underline;
  }
  .duke-tiptap-editor.tiptap ul,
  .duke-tiptap-editor.tiptap ol {
    padding: 0 1.25rem;
  }
  .duke-tiptap-editor.tiptap ul {
    list-style-type: disc;
  }
  .duke-tiptap-editor.tiptap ol {
    list-style-type: decimal;
  }
  .duke-tiptap-editor.tiptap code {
    background-color: rgba(97, 97, 97, 0.1);
    color: #616161;
  }
  .duke-tiptap-editor.tiptap pre {
    background: #0d0d0d;
    color: #fff;
    font-family: 'JetBrainsMono', monospace;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
  }
  .duke-tiptap-editor.tiptap pre code {
    color: inherit;
    padding: 0;
    background: none;
    font-size: 0.8rem;
  }
  .duke-tiptap-editor.tiptap img {
    max-width: 80%;
    height: auto;
    display: inline;
  }
  .duke-tiptap-editor.tiptap div.ProseMirror-selectednode img {
    outline: 2px solid #563fd8;
  }
  .duke-tiptap-editor.tiptap table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    margin: 0;
    overflow: hidden;
  }
  .duke-tiptap-editor.tiptap table td,
  .duke-tiptap-editor.tiptap table th {
    min-width: 1em;
    border: 1px solid #d0d5dd;
    padding: 0.5rem 0.75rem;
    vertical-align: top;
    box-sizing: border-box;
    position: relative;
  }
  .duke-tiptap-editor.tiptap table td > *,
  .duke-tiptap-editor.tiptap table th > * {
    margin-bottom: 0;
  }
  .duke-tiptap-editor.tiptap table th {
    font-weight: 600;
    text-align: left;
    background-color: #f9fafb;
  }
  .duke-tiptap-editor.tiptap table .selectedCell::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(86, 63, 216, 0.08);
    pointer-events: none;
    z-index: 2;
  }
  .duke-tiptap-editor.tiptap table .column-resize-handle {
    position: absolute;
    right: -2px;
    top: 0;
    bottom: -2px;
    width: 4px;
    background-color: #563fd8;
    pointer-events: none;
    z-index: 1;
  }
  .duke-tiptap-editor.tiptap .tableWrapper {
    overflow-x: auto;
    margin: 1rem 0;
  }
  .duke-tiptap-editor.tiptap blockquote {
    padding-left: 1rem;
    border-left: 2px solid rgba(13, 13, 13, 0.1);
    color: #6a737d;
    quotes: none;
  }
  .duke-tiptap-editor.tiptap hr {
    border: none;
    border-top: 2px solid rgba(13, 13, 13, 0.1);
    margin: 2rem 0;
  }
  .tiptap p.is-empty::before {
    color: #adb5bd;
    font-style: italic;
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }
  .duke-tiptap-readonly {
    margin: 0;
  }
  .duke-tiptap-readonly img {
    outline: none !important;
  }
  .duke-tiptap-default {
    margin: 0 2rem;
  }
  .display-block {
    display: block;
  }
  .hidden {
    display: none;
  }
`;

export const useEditorStyles = () => {
  useEffect(() => {
    // Create style element if it doesn't exist
    const styleId = 'duke-tiptap-styles';
    if (!document.getElementById(styleId)) {
      const styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = editorStyles.styles;
      document.head.appendChild(styleElement);

      // Cleanup on unmount
      return () => {
        const existingStyle = document.getElementById(styleId);
        if (existingStyle) {
          document.head.removeChild(existingStyle);
        }
      };
    }
  }, []);
};
